import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../data/mock';

const TechnologySection = () => {
  const { t } = useLanguage();

  return (
    <section id="technology" className="py-24 bg-gradient-to-br from-gray-50 via-blue-50/20 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
          <div className="order-2 lg:order-1">
            <div className="inline-block bg-[#1c3855]/10 text-[#1c3855] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Canon Colorado Technology
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {t.printing.title}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t.printing.description}
            </p>
            <div className="space-y-4">
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10">
                <h4 className="font-bold text-[#1c3855] mb-2">UVGel 860 Ink Technology</h4>
                <p className="text-gray-700 text-sm">Advanced ink set with instant-dry, odorless output and exceptional scratch resistance</p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10">
                <h4 className="font-bold text-[#1c3855] mb-2">Scratch-Resistant Prints</h4>
                <p className="text-gray-700 text-sm">High mechanical robustness and chemical resistance for long-lasting durability</p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10">
                <h4 className="font-bold text-[#1c3855] mb-2">Vivid & Bold Colors</h4>
                <p className="text-gray-700 text-sm">Wider color gamut with UVGel FullBeam Curing for stunning visual impact</p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10">
                <h4 className="font-bold text-[#1c3855] mb-2">Dimensional Stability</h4>
                <p className="text-gray-700 text-sm">Low-temperature curing preserves canvas texture and maintains perfect dimensions</p>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img
                src={images.printing}
                alt="Canon Colorado Printing"
                className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c3855]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
          <div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img
                src={images.packaging}
                alt="Custom Packaging"
                className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c3855]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
          <div>
            <div className="inline-block bg-[#1c3855]/10 text-[#1c3855] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Custom Packaging
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {t.packaging.title}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t.packaging.description}
            </p>
            <ul className="space-y-4">
              {['Exact Size Boxes', 'Reduced Damage Rate', 'Space Optimization', 'Cost Efficiency'].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#1c3855] flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
          <div className="order-2 lg:order-1">
            <div className="inline-block bg-[#1c3855]/10 text-[#1c3855] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Frame Options
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {t.frames.title}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t.frames.description}
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10 text-center">
                <div className="w-full h-12 bg-gradient-to-r from-amber-900 to-amber-700 rounded-lg mb-2"></div>
                <span className="text-sm font-semibold text-gray-900">Walnut</span>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10 text-center">
                <div className="w-full h-12 bg-gradient-to-r from-amber-200 to-amber-100 rounded-lg mb-2"></div>
                <span className="text-sm font-semibold text-gray-900">Natural</span>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10 text-center">
                <div className="w-full h-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg mb-2"></div>
                <span className="text-sm font-semibold text-gray-900">Black</span>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-[#1c3855]/10 text-center">
                <div className="w-full h-12 bg-gradient-to-r from-gray-100 to-white rounded-lg mb-2 border border-gray-300"></div>
                <span className="text-sm font-semibold text-gray-900">White</span>
              </div>
            </div>
            <ul className="space-y-4">
              {['Gallery Wrapped Canvas', 'Floating Frame (4 Colors)', 'Ready to Hang • Mounting Hardware Included', 'Extra Large Sizes Available'].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#1c3855] flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img
                src={images.frames}
                alt="Gallery Wrapped Frames"
                className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c3855]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img
                src={images.shipping}
                alt="Shipping & Logistics"
                className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c3855]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
          <div>
            <div className="inline-block bg-[#1c3855]/10 text-[#1c3855] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Shipping & Logistics
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {t.shipping.title}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t.shipping.description}
            </p>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <h4 className="font-bold text-green-900">Damage Guarantee</h4>
                    <p className="text-sm text-green-700">Free replacement if any damage occurs in transit</p>
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {['5 Business Days Production', 'FedEx Express Shipping', 'Full Insurance Coverage', 'USA & Canada Delivery'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#1c3855] flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
